import memoize from 'lodash/memoize';

import { getDirectoryEntryImage } from '@lumapps/directory-entries/utils';

(function IIFE() {
    'use strict';

    /////////////////////////////

    function DirectoryAdminController($scope, ConfigTheme, DataTable, Directory, DirectoryEntry, Instance,
        LxDropdownService, LxNotificationService, Metadata, Translation, Utils) {
        'ngInject';

        var vm = this;

        /////////////////////////////
        //                         //
        //    Private attributes   //
        //                         //
        /////////////////////////////

        /**
         *  The id of the current directory.
         *
         * @type {string}
         * @constant
         * @readonly
         */
        var _DIRECTORY_ID;

        /**
         * The dropdown filter identifier.
         *
         * @type {string}
         * @constant
         * @readonly
         */
        vm.DROPDOWN_ID = 'admin-filters';

        /**
         * The dropdown filter opening target.
         *
         * @type {string}
         * @constant
         * @readonly;
         */
        vm.DROPDOWN_TARGET = 'admin-search-field';

        /**
         * The fields to use in the projection for the entry list.
         *
         * @type {Object}
         * @constant
         * @readonly
         */
        var _ENTRY_LIST_PROJECTION = {
            items: {
                deletableFromFavorite: true,
                directory: true,
                feedKeys: true,
                id: true,
                isInFavoriteFeedKeys: true,
                link: true,
                metadata: true,
                name: true,
                order: true,
                tags: true,
                thumbnail: true,
                uid: true,
                values: true,
            },
        };

        /////////////////////////////
        //                         //
        //    Public attributes    //
        //                         //
        /////////////////////////////

        /**
         * The id of the directory entry dialog.
         *
         * @type {string}
         * @constant
         * @readonly
         */
        vm.ENTRY_DIALOG_ID = 'directory-entry-settings';

        /**
         * The sort order for the data table.
         *
         * @type {string}
         */
        vm.dataTableSortOrder = 'name';

        /**
         * Whether to display the filter for the data table or not.
         *
         * @type {boolean}
         */
        vm.displayFilter = false;

        /////////////////////////////

        /**
         * Services and utilities.
         */
        vm.ConfigTheme = ConfigTheme;
        vm.DataTable = DataTable;
        vm.Directory = Directory;
        vm.DirectoryEntry = DirectoryEntry;
        vm.Metadata = Metadata;
        vm.Utils = Utils;

        /////////////////////////////
        //                         //
        //    Private functions    //
        //                         //
        /////////////////////////////

        /**
         * Indicates if there are some filters being applied to the list of directory entries or not.
         *
         * @return {boolean} Whether there are some filters applied to the current list.
         */
        function _computeHasActiveFilter() {
            var filter = angular.fastCopy(vm.filter);
            var hasMetadata = _.some(_.values(filter.metadata), angular.isDefinedAndFilled);

            if (!hasMetadata) {
                filter.metadata = {};
            }

            return _.some(_.values(filter), angular.isDefinedAndFilled);
        }

        /**
         * Filter directory entries.
         */
        function _filterDirectory() {
            var combinedMetadata = [];

            // Clear currently selected items.
            DataTable.resetSelectedItems();

            angular.forEach(vm.filter.metadata, function forEachFilterMetadata(metadata) {
                if (angular.isDefinedAndFilled(metadata) && angular.isArray(metadata)) {
                    combinedMetadata.push({
                        metadata: metadata,
                    });
                }
            });

            vm.hasActiveFilter = _computeHasActiveFilter();

            DirectoryEntry.filterize({
                combinedMetadata: combinedMetadata,
                directory: _DIRECTORY_ID,
                displaySortMandatory: false,
                includeUserEntries: false,
                query: vm.filter.title,
                sortOrder: vm.dataTableSortOrder,
                tags: vm.filter.tags,
            }, undefined, undefined, undefined, _ENTRY_LIST_PROJECTION);
        }

        /////////////////////////////
        //                         //
        //     Public functions    //
        //                         //
        /////////////////////////////

        /**
         * Open the dialog to create a new entry.
         */
        function addEntry() {
            DirectoryEntry.setCurrent({
                directory: _DIRECTORY_ID,
                metadata: {},
                values: {},
            });

            Utils.waitForAndExecute('#' + vm.ENTRY_DIALOG_ID);
        }

        /**
         * Delete selected items in the data table.
         */
        function deleteSelectedItems() {
            LxNotificationService.confirm(
                Translation.translate('ADMIN_DELETE_SELECTED_ITEMS'),
                Translation.translate('ADMIN_DELETE_SELECTED_ITEMS_DESCRIPTION'), {
                    cancel: Translation.translate('CANCEL'),
                    ok: Translation.translate('OK'),
                }, function onDeletionConfirm(answer) {
                    if (!answer) {
                        return;
                    }

                    // Only delete entries that are currently selected.
                    DirectoryEntry.delMulti(angular.fastCopy(DataTable.getSelectedItems(true)),
                        function onDeleteMultiDirectoryEntrySuccess(response) {
                            if (angular.isDefinedAndFilled(response.errors)) {
                                var error = {
                                    data: {
                                        error: {
                                            message: response.errors[0].reason,
                                        },
                                    },
                                };
                                Utils.displayServerError(error);

                                return;
                            }

                            DataTable.refresh(DirectoryEntry.getList());
                            LxNotificationService.success(
                                Translation.translate('ADMIN_DELETE_SELECTED_ITEMS_SUCCESS')
                            );
                        }, Utils.displayServerError);
                });
        }

        /**
         * Edit an existing entry.
         */
        function editEntry() {
            var entry = DataTable.getFirstSelectedItem();

            if (angular.isUndefinedOrEmpty(entry)) {
                return;
            }

            DirectoryEntry.setCurrent(DirectoryEntry.formatServerObjectToClient(angular.fastCopy(entry)));

            Utils.waitForAndExecute('#' + vm.ENTRY_DIALOG_ID);
        }

        /**
         * Get the directory entry admin settings dialog path.
         *
         * @return {string} The path to the directory admin template.
         */
        function getDirectoryEntryAdminSettingsPath() {
            return (ConfigTheme.HAS_CUSTOM_DIRECTORY_ENTRY_SETTINGS) ?
                '/client/front-office/specifics/overrides/' +
                    Instance.getInstance().override +
                    '/modules/module/modules/directory/views/directory-entry-admin.html' :
                '/client/common/modules/module/modules/directory/views/directory-entry-admin.html';
        }

        /**
         * Get sort order on given field.
         *
         * @param  {string} field The field to check the order of.
         * @return {string} The field sort order.
         */
        function getSortOrder(field) {
            if (vm.dataTableSortOrder === field) {
                return 'asc';
            } else if (vm.dataTableSortOrder === `-${field}`) {
                return 'desc';
            }

            return '';
        }

        /**
         * Open filters.
         *
         * @param {Event} evt The click event.
         */
        function openFilters(evt) {
            LxDropdownService.open(vm.DROPDOWN_ID, '#' + vm.DROPDOWN_TARGET);

            evt.stopPropagation();
        }

        /**
         * Toggle filter.
         */
        function toggleFilter() {
            vm.displayFilter = !vm.displayFilter;
        }

        /**
         * Toggle sort order.
         *
         * @param {string} sortOrder The sort order to be toggled.
         */
        function toggleSortOrder(sortOrder) {
            if (angular.isUndefined(vm.dataTableSortOrder) || sortOrder !== vm.dataTableSortOrder) {
                vm.dataTableSortOrder = sortOrder;
            } else {
                vm.dataTableSortOrder = '-' + sortOrder;
            }

            _filterDirectory();
        }

        const getDirectoryEntryImg = memoize(getDirectoryEntryImage, (entry) => {
            return entry.thumbnail;
        });

        /////////////////////////////

        vm.addEntry = addEntry;
        vm.deleteSelectedItems = deleteSelectedItems;
        vm.editEntry = editEntry;
        vm.getDirectoryEntryAdminSettingsPath = getDirectoryEntryAdminSettingsPath;
        vm.getSortOrder = getSortOrder;
        vm.openFilters = openFilters;
        vm.toggleFilter = toggleFilter;
        vm.toggleSortOrder = toggleSortOrder;
        vm.getDirectoryEntryImage = getDirectoryEntryImg;

        /////////////////////////////
        //                         //
        //          Events         //
        //                         //
        /////////////////////////////

        /**
         * When the controller is destroyed, destroy also the data table.
         */
        $scope.$on('$destroy', function onDestroy() {
            DataTable.destroy();
        });

        /////////////////////////////
        //                         //
        //        Watchers         //
        //                         //
        /////////////////////////////

        /**
         * Watch for any changes in the filters and refresh the list of directory entries.
         * Debounce the refreshing so that we don't refresh to often.
         *
         * @param {Object} newValue The new filter object.
         * @param {Object} oldValue The old filter object.
         */
        $scope.$watch('vm.filter', function onFilterChange(newValue, oldValue) {
            if (newValue !== oldValue) {
                _filterDirectory();
            }
        }, true);

        /////////////////////////////

        /**
         * Initialize the controller.
         */
        function init() {
            _DIRECTORY_ID = Directory.getCurrent().id;

            // Init default filter.
            vm.filter = {
                feeds: [],
                metadata: {},
                tags: [],
                title: undefined,
            };

            DirectoryEntry.filterize({
                directory: _DIRECTORY_ID,
                displaySortMandatory: false,
                includeUserEntries: false,
                sortOrder: vm.dataTableSortOrder,
            }, function filterizeCb() {
                // Set our data table with the retrieved entries.

                /*
                 * Warning: here I'm passing a function that return a function call that return a list, this is to keep
                 * the reference to that list and avoid headache.
                 */
                DataTable.init(function initDataTable() {
                    return DirectoryEntry.getList();
                }, 'uid');
            }, undefined, undefined, _ENTRY_LIST_PROJECTION);
        }
        init();
    }

    angular.module('Controllers').controller('DirectoryAdminController', DirectoryAdminController);
})();
